/**
 * Leviathan Web - Driver Form Component
 * Quasar Prime LLC
 * Reusable form for creating and editing drivers
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';

interface DriverFormData {
  driverNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  employeeId: string;
  hireDate: string;
  employmentStatus: 'ACTIVE' | 'ON_LEAVE' | 'TERMINATED' | 'RETIRED';
  licenseNumber: string;
  licenseState: string;
  licenseClass: string;
  licenseExpiry: string;
  hazmatCertified: boolean;
  hazmatExpiry: string;
  tankerEndorsement: boolean;
  twicCard: boolean;
  twicExpiry: string;
  medicalCardExpiry: string;
  status: 'AVAILABLE' | 'ON_DUTY' | 'OFF_DUTY' | 'VACATION' | 'SICK' | 'SUSPENDED';
  notes: string;
}

interface DriverFormProps {
  driverId?: string;
  initialData?: Partial<DriverFormData>;
  onSuccess?: () => void;
}

// US States
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// License classes
const LICENSE_CLASSES = [
  'Class A',
  'Class B',
  'Class C',
  'CDL-A',
  'CDL-B',
  'CDL-C',
];

export default function DriverForm({ driverId, initialData, onSuccess }: DriverFormProps) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<DriverFormData>({
    driverNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    employeeId: '',
    hireDate: '',
    employmentStatus: 'ACTIVE',
    licenseNumber: '',
    licenseState: '',
    licenseClass: '',
    licenseExpiry: '',
    hazmatCertified: false,
    hazmatExpiry: '',
    tankerEndorsement: false,
    twicCard: false,
    twicExpiry: '',
    medicalCardExpiry: '',
    status: 'AVAILABLE',
    notes: '',
    ...initialData,
  });

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName) {
        throw new Error('First name and last name are required');
      }

      const payload = {
        ...formData,
        hireDate: formData.hireDate || null,
        licenseExpiry: formData.licenseExpiry || null,
        hazmatExpiry: formData.hazmatExpiry || null,
        twicExpiry: formData.twicExpiry || null,
        medicalCardExpiry: formData.medicalCardExpiry || null,
      };

      const url = driverId
        ? `${API_URL}/api/drivers/${driverId}`
        : `${API_URL}/api/drivers`;

      const response = await fetch(url, {
        method: driverId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/drivers');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save driver');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
        </div>
      )}

      {/* Basic Information */}
      <FormSection title="Personal Information">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField label="Driver Number">
            <input
              type="text"
              name="driverNumber"
              value={formData.driverNumber}
              onChange={handleChange}
              placeholder={driverId ? '' : 'Auto-generated if blank'}
              className="form-input"
            />
          </FormField>

          <FormField label="First Name" required>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="form-input"
            />
          </FormField>

          <FormField label="Last Name" required>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="form-input"
            />
          </FormField>

          <FormField label="Employee ID">
            <input
              type="text"
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              placeholder="Internal employee #"
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Contact Information */}
      <FormSection title="Contact Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Email">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="driver@example.com"
              className="form-input"
            />
          </FormField>

          <FormField label="Phone">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 555-5555"
              className="form-input"
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField label="Address">
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street address"
                className="form-input"
              />
            </FormField>
          </div>

          <FormField label="City">
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="State">
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">-- Select --</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="ZIP">
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                className="form-input"
              />
            </FormField>
          </div>
        </div>
      </FormSection>

      {/* Employment */}
      <FormSection title="Employment">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Hire Date">
            <input
              type="date"
              name="hireDate"
              value={formData.hireDate}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="Employment Status">
            <select
              name="employmentStatus"
              value={formData.employmentStatus}
              onChange={handleChange}
              className="form-input"
            >
              <option value="ACTIVE">Active</option>
              <option value="ON_LEAVE">On Leave</option>
              <option value="TERMINATED">Terminated</option>
              <option value="RETIRED">Retired</option>
            </select>
          </FormField>

          <FormField label="Current Status">
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-input"
            >
              <option value="AVAILABLE">Available</option>
              <option value="ON_DUTY">On Duty</option>
              <option value="OFF_DUTY">Off Duty</option>
              <option value="VACATION">Vacation</option>
              <option value="SICK">Sick</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </FormField>
        </div>
      </FormSection>

      {/* License Information */}
      <FormSection title="License Information">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField label="License Number">
            <input
              type="text"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>

          <FormField label="License State">
            <select
              name="licenseState"
              value={formData.licenseState}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">-- Select --</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="License Class">
            <select
              name="licenseClass"
              value={formData.licenseClass}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">-- Select --</option>
              {LICENSE_CLASSES.map((cls) => (
                <option key={cls} value={cls}>
                  {cls}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="License Expiry">
            <input
              type="date"
              name="licenseExpiry"
              value={formData.licenseExpiry}
              onChange={handleChange}
              className="form-input"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Certifications */}
      <FormSection title="Certifications & Endorsements">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="hazmatCertified"
                checked={formData.hazmatCertified}
                onChange={handleChange}
                className="w-5 h-5 bg-background border-2 border-panel-border checked:bg-amber checked:border-amber focus:ring-amber"
              />
              <label className="text-text-primary font-mono">HAZMAT Certified</label>
            </div>
            {formData.hazmatCertified && (
              <FormField label="HAZMAT Expiry">
                <input
                  type="date"
                  name="hazmatExpiry"
                  value={formData.hazmatExpiry}
                  onChange={handleChange}
                  className="form-input"
                />
              </FormField>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="tankerEndorsement"
                checked={formData.tankerEndorsement}
                onChange={handleChange}
                className="w-5 h-5 bg-background border-2 border-panel-border checked:bg-amber checked:border-amber focus:ring-amber"
              />
              <label className="text-text-primary font-mono">Tanker Endorsement</label>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="twicCard"
                checked={formData.twicCard}
                onChange={handleChange}
                className="w-5 h-5 bg-background border-2 border-panel-border checked:bg-amber checked:border-amber focus:ring-amber"
              />
              <label className="text-text-primary font-mono">TWIC Card</label>
            </div>
            {formData.twicCard && (
              <FormField label="TWIC Expiry">
                <input
                  type="date"
                  name="twicExpiry"
                  value={formData.twicExpiry}
                  onChange={handleChange}
                  className="form-input"
                />
              </FormField>
            )}
          </div>
        </div>

        <div className="mt-6">
          <FormField label="Medical Card Expiry">
            <input
              type="date"
              name="medicalCardExpiry"
              value={formData.medicalCardExpiry}
              onChange={handleChange}
              className="form-input max-w-xs"
            />
          </FormField>
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes">
        <FormField label="Additional Notes">
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Any additional notes about this driver..."
            className="form-input resize-none"
          />
        </FormField>
      </FormSection>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/drivers')}
          className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isLoading}
          className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
          style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
        >
          <span className="relative z-10">
            {isLoading ? 'Saving...' : driverId ? 'Update Driver' : 'Create Driver'}
          </span>
          <div
            className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ mixBlendMode: 'overlay' }}
          />
        </button>
      </div>
    </form>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label} {required && <span className="text-amber">*</span>}
      </label>
      {children}
    </div>
  );
}
